import { useCallback, useEffect, useRef, useState } from "react";
import { Grid3x3, Clock3, HandCoins, Wallet, CalendarOff, CalendarRange, KanbanSquare, HeartPulse, Rocket, BarChart3, Sparkles } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Mic2, LogOut, IndianRupee,
  ReceiptText, UserCircle2, Percent, Settings as SettingsIcon,
  ChevronDown, Target, UserPlus, ListChecks, TrendingUp, ClipboardCheck, UserCog, Upload, History, ShieldCheck, CalendarDays, ClipboardList, CalendarX, PauseCircle, Smartphone, MessagesSquare, Megaphone, BellRing, Link2, AlertTriangle, PiggyBank, Fingerprint, CreditCard, Gift, RefreshCcw, PlayCircle, Trophy, Flag, Banknote, CalendarClock, Package,
  Menu, X, MoreHorizontal, AudioLines,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { atLeast, ROLE_LABELS } from "@/lib/roles";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Brand } from "@/components/Brand";
import { getCashDesk, getWalkinWaiting, leavePendingCount } from "@/lib/api";
import { FacilitySwitcher } from "@/components/FacilitySwitcher";
import { PendingPaymentsReminder } from "@/components/PendingPaymentsReminder";
import { NoticesBell } from "@/components/NoticesBell";
import { BirthdayPrompt } from "@/components/StaffBirthday";
import { listGhlConnections } from "@/lib/api";

// The left nav is only for the selected facility. Cross-gym work (Lead Nurture, Global Admin)
// is under "Across every gym" in the header's Facility dropdown (components/FacilitySwitcher.jsx).
// Every nav item declares the minimum role that can see it.
// user < manager < admin < master_admin
const MAIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, min: "user", testid: "nav-dashboard" },
  { to: "/numbers", label: "Gym numbers", icon: BarChart3, min: "manager", testid: "nav-numbers" },
  { to: "/ask", label: "Ask your data", icon: Sparkles, min: "manager", testid: "nav-ask" },
  { to: "/customers", label: "Members", icon: UserCircle2, min: "user", testid: "nav-customers" },
  { to: "/sales-log", label: "All Sales", icon: ReceiptText, min: "user", testid: "nav-all-sales" },
  { to: "/expenses", label: "Expenses", icon: IndianRupee, min: "user", testid: "nav-expenses" },
  { to: "/requests", label: "Refunds & Purchases", icon: HandCoins, min: "user", testid: "nav-requests" },
  { to: "/rent-fund", label: "Rent Fund", icon: PiggyBank, min: "manager", testid: "nav-rent-fund", feature: "rent_fund_enabled" },
  { to: "/check-in", label: "Check-In (Tablet)", icon: ClipboardList, min: "user", testid: "nav-check-in" },
  { to: "/board", label: "Board", icon: KanbanSquare, min: "user", testid: "nav-board" },
  { to: "/roster", label: "Roster", icon: CalendarRange, min: "user", testid: "nav-roster" },
  { to: "/work", label: "Staff Clock-In", icon: Clock3, min: "user", testid: "nav-work" },
  { to: "/leave", label: "Leave", icon: CalendarOff, min: "user", testid: "nav-leave" },
];

const FE_NAV = [
  { to: "/sales", label: "New Consult", icon: Target, min: "user", end: true, testid: "nav-fe-sales" },
  { to: "/sales/dashboard", label: "Monthly Sales Dashboard", icon: LayoutDashboard, min: "user", testid: "nav-fe-monthly" },
  { to: "/sales/fe-conversion", label: "FE Retention Dashboard", icon: TrendingUp, min: "user", testid: "nav-fe-conversion" },
  { to: "/sales/accountability", label: "Accountability", icon: ClipboardCheck, min: "user", testid: "nav-accountability" },
  { to: "/trial-week", label: "Trial Week", icon: Sparkles, min: "user", testid: "nav-trial-week" },
  { to: "/uru-passes", label: "URU Passes", icon: Sparkles, min: "manager", testid: "nav-uru-passes" },
  { to: "/sales/qc", label: "QC Checks", icon: ShieldCheck, min: "user", testid: "nav-qc" },
  { to: "/leads", label: "Leads", icon: ListChecks, min: "user", testid: "nav-leads" },
  { to: "/incentives", label: "Incentives", icon: Percent, min: "user", testid: "nav-incentives" },
  { to: "/recordings", label: "Recordings", icon: Mic2, min: "manager", testid: "nav-recordings" },
];

const RETENTION_NAV = [
  { to: "/retention", label: "Member Manager", icon: UserCog, min: "user", testid: "nav-retention-mm" },
  { to: "/at-risk", label: "At-risk members", icon: HeartPulse, min: "user", testid: "nav-at-risk" },
  { to: "/upsell", label: "Upsell", icon: Rocket, min: "user", testid: "nav-upsell" },
  { to: "/events", label: "Events", icon: CalendarDays, min: "user", testid: "nav-events" },
  { to: "/renewal-reminders", label: "Renewal Reminders", icon: BellRing, min: "manager", testid: "nav-renewal-reminders" },
  { to: "/app-renewals", label: "App Renewals", icon: RefreshCcw, min: "manager", testid: "nav-app-renewals" },
  { to: "/cash-payments", label: "Cash Payments", icon: Banknote, min: "user", testid: "nav-cash-payments" },
  { to: "/payment-plans", label: "Payment Plans", icon: CalendarClock, min: "user", testid: "nav-payment-plans" },
  { to: "/reward-points", label: "Reward Points", icon: Gift, min: "manager", testid: "nav-reward-points" },
  { to: "/no-shows", label: "No-Show Follow-ups", icon: CalendarX, min: "user", testid: "nav-no-shows" },
];

const MEMBER_APP_NAV = [
  { to: "/member-app/inbox", label: "Coach Inbox", icon: MessagesSquare, min: "user", testid: "nav-member-app-inbox" },
  { to: "/member-app/plan-reviews", label: "Plan Reviews", icon: ClipboardCheck, min: "user", testid: "nav-member-app-plan-reviews" },
  { to: "/member-app/programmes", label: "Programmes", icon: ClipboardList, min: "user", testid: "nav-member-app-programmes" },
  { to: "/member-app/exercises", label: "Exercise Library", icon: PlayCircle, min: "user", testid: "nav-member-app-exercises" },
  { to: "/member-app/referrals", label: "Referrals", icon: Gift, min: "user", testid: "nav-member-app-referrals" },
  { to: "/member-app/clients", label: "App Clients", icon: Smartphone, min: "user", testid: "nav-member-app-clients" },
  { to: "/member-app/challenges", label: "Monthly Challenges", icon: Flag, min: "user", testid: "nav-member-app-challenges" },
  { to: "/member-app/bingo", label: "BINGO", icon: Grid3x3, min: "user", testid: "nav-member-app-bingo" },
  { to: "/member-app/rewards", label: "Member Rewards", icon: Trophy, min: "user", testid: "nav-member-app-rewards" },
  { to: "/member-app/pauses", label: "Pauses", icon: PauseCircle, min: "user", testid: "nav-member-app-pauses" },
  { to: "/member-app/closures", label: "Closures & Holidays", icon: CalendarOff, min: "user", testid: "nav-member-app-closures" },
  { to: "/member-app/announcements", label: "Announcements", icon: Megaphone, min: "manager", testid: "nav-member-app-announcements" },
];

const ADMIN_NAV = [
  { to: "/users", label: "User Management", icon: UserPlus, min: "manager", testid: "nav-users" },
  { to: "/ghl", label: "GoHighLevel", icon: Link2, min: "admin", testid: "nav-ghl" },
  { to: "/payments", label: "Payments", icon: CreditCard, min: "master_admin", testid: "nav-payments" },
  { to: "/bulk-import", label: "Bulk Import", icon: Upload, min: "manager", testid: "nav-bulk-import" },
  { to: "/audit-log", label: "Audit Log", icon: History, min: "manager", testid: "nav-audit-log" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, min: "manager", testid: "nav-settings" },
];

// Phones: the side menu becomes a slide-out drawer, and these sit in a bottom bar.
const BOTTOM_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/sales", label: "Consult", icon: Target, end: true },
  { to: "/sales-log", label: "All Sales", icon: ReceiptText },
  { to: "/sales/accountability", label: "Accountability", icon: ClipboardCheck },
  { to: "/work", label: "Clock in", icon: Clock3 },
];

export default function AppShell() {
  const loc = useLocation();
  const navTo = useNavigate();
  const { user, logout, effectiveRole, currency, activeFacility, viewOnly } = useAuth();
  // Walk-in forms waiting for a consult at this gym: a count on the New Consult menu item.
  const [waitingCount, setWaitingCount] = useState(0);
  const [leaveCount, setLeaveCount] = useState(0);         // leave requests waiting for this gym's facility head
  useEffect(() => {
    const check = () => {
      getWalkinWaiting().then((d) => setWaitingCount((d.forms || []).filter((f) => f.status === "new").length)).catch(() => {});
      leavePendingCount().then((d) => setLeaveCount(d.count || 0)).catch(() => {});
    };
    check();                                             // always on open / page change
    const t = setInterval(() => document.visibilityState === "visible" && check(), 60000);
    window.addEventListener("leave-changed", check);
    return () => { clearInterval(t); window.removeEventListener("leave-changed", check); };
  }, [activeFacility?.id, loc.pathname]);
  // Cash taken at the desk that still needs saving here (and, for master admins, cash held over 2 days).
  const [cashCount, setCashCount] = useState(0);
  const onCashPage = loc.pathname === "/cash-payments";
  useEffect(() => {
    const check = () => getCashDesk().then((d) => setCashCount(d.waiting.length + (d.is_master_admin ? d.held.filter((h) => h.late).length : 0))).catch(() => {});
    check();                                             // on opening, switching gym, or leaving the Cash page
    const t = setInterval(() => document.visibilityState === "visible" && check(), 60000);
    return () => clearInterval(t);
  }, [activeFacility?.id, onCashPage]);
  const role = effectiveRole || user?.role;
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Pages with their own sticky bars sit just under this header: publish its height.
  const headerObserver = useRef(null);
  const headerRef = useCallback((el) => {
    headerObserver.current?.disconnect();
    if (!el || typeof ResizeObserver === "undefined") return;
    const publish = () => document.documentElement.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
    publish();
    headerObserver.current = new ResizeObserver(publish);
    headerObserver.current.observe(el);
  }, []);
  // Close the phone drawer whenever the page changes.
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);
  // Esc closes it too; and don't let the page behind scroll while it's open.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // Sync the active currency symbol to the window so non-React formatters can read it.
  // AuthContext already guarantees `currency` is a sensible non-empty string (symbol → code → ₹),
  // so we set it as-is instead of forcing another ₹ fallback here.
  useEffect(() => {
    if (currency) window.__ACTIVE_CURRENCY_SYMBOL__ = currency;
  }, [currency]);

  // Global UX: when a number/text input holding a stray default "0" is focused, select it so
  // typing replaces it immediately (fixes "the 0 doesn't go away" issue on every form).
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (!t || t.tagName !== "INPUT") return;
      if (t.type !== "number" && t.type !== "text") return;
      const v = t.value;
      if (v === "0" || v === "0.0" || v === "0.00") {
        // defer so React state-driven controlled inputs don't blow the selection away
        setTimeout(() => { try { t.select(); } catch (_) { /* ignore */ } }, 0);
      }
    };
    document.addEventListener("focusin", handler, true);
    // Silence the benign "ResizeObserver loop completed with undelivered notifications" warning
    // that Radix Popover / react-day-picker occasionally throw. It's harmless but CRA's dev error
    // overlay treats it as fatal, hiding whatever the user was doing behind a red screen.
    const swallow = (e) => {
      const msg = (e && (e.message || e.reason?.message)) || "";
      if (typeof msg === "string" && msg.startsWith("ResizeObserver loop")) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
      }
    };
    window.addEventListener("error", swallow, true);
    window.addEventListener("unhandledrejection", swallow, true);
    return () => {
      document.removeEventListener("focusin", handler, true);
      window.removeEventListener("error", swallow, true);
      window.removeEventListener("unhandledrejection", swallow, true);
    };
  }, []);

  // `feature` items only show for gyms that have that feature switched on (e.g. Rent Fund: Dubai).
  const mainNav = MAIN_NAV.filter((n) => atLeast(role, n.min) && (!n.feature || activeFacility?.[n.feature]));
  const feNav = FE_NAV.filter((n) => atLeast(role, n.min));
  const retentionNav = RETENTION_NAV.filter((n) => atLeast(role, n.min));
  const memberAppNav = MEMBER_APP_NAV.filter((n) => atLeast(role, n.min));
  const adminNav = ADMIN_NAV.filter((n) => atLeast(role, n.min));

  const feActiveHint = (feNav || []).some((n) => loc.pathname.startsWith(n.to));
  const [feOpen, setFeOpen] = useState(feActiveHint || feNav.length > 0);
  const retentionActiveHint = (retentionNav || []).some((n) => loc.pathname.startsWith(n.to));
  const [retentionOpen, setRetentionOpen] = useState(retentionActiveHint || true);
  const [memberAppOpen, setMemberAppOpen] = useState(true);

  const allNav = [...mainNav, ...feNav, ...retentionNav, ...memberAppNav, ...adminNav];
  const title = loc.pathname.startsWith("/recordings/")
    ? "Call detail"
    : loc.pathname.startsWith("/global-admin") ? "Global Admin"
    : loc.pathname === "/profile" ? "My profile"
    : (allNav.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)))?.label || "FullPowerAnalytics");

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  // Kiosk mode: tablet check-in full-screen — no sidebar, no header, no reminders.
  const kiosk = loc.pathname === "/check-in" && new URLSearchParams(loc.search).get("kiosk") === "1";

  const sidebar = (
    <>
      <div className="px-5 pt-6 pb-6 border-b border-[#1a1a1a] shrink-0">
        <Brand size={38} />
      </div>

      <nav className="px-3 py-5 flex-1 min-h-0 overflow-y-auto">
        {mainNav.length > 0 && (
          <>
            <div className="eyebrow px-3 mb-3">Main</div>
            <ul className="space-y-1">
              {(mainNav || []).map((n) => <NavItem key={n.to} {...n} badge={n.to === "/leave" ? leaveCount : 0} />)}
            </ul>
          </>
        )}

        {feNav.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setFeOpen((s) => !s)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-left"
              data-testid="fe-section-toggle"
            >
              <div className="eyebrow">Front End Challenge</div>
              <ChevronDown className={`h-3.5 w-3.5 text-zinc-500 transition-transform ${feOpen ? "" : "-rotate-90"}`} />
            </button>
            {feOpen && (
              <ul className="space-y-1 mt-2">
                {(feNav || []).map((n) => <NavItem key={n.to} {...n} badge={n.to === "/sales" ? waitingCount : 0} />)}
              </ul>
            )}
          </div>
        )}

        {retentionNav.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setRetentionOpen((s) => !s)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-left"
              data-testid="retention-section-toggle"
            >
              <div className="eyebrow">Retention</div>
              <ChevronDown className={`h-3.5 w-3.5 text-zinc-500 transition-transform ${retentionOpen ? "" : "-rotate-90"}`} />
            </button>
            {retentionOpen && (
              <ul className="space-y-1 mt-2">
                {(retentionNav || []).map((n) => <NavItem key={n.to} {...n} badge={n.to === "/cash-payments" ? cashCount : 0} badgeTitle="cash to save or deposit" />)}
              </ul>
            )}
          </div>
        )}

        {memberAppNav.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setMemberAppOpen((s) => !s)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-left"
              data-testid="member-app-section-toggle"
            >
              <div className="eyebrow">Member App</div>
              <ChevronDown className={`h-3.5 w-3.5 text-zinc-500 transition-transform ${memberAppOpen ? "" : "-rotate-90"}`} />
            </button>
            {memberAppOpen && (
              <ul className="space-y-1 mt-2">
                {(memberAppNav || []).map((n) => <NavItem key={n.to} {...n} />)}
              </ul>
            )}
          </div>
        )}

        {adminNav.length > 0 && (
          <div className="mt-6">
            <div className="eyebrow px-3 mb-3">Admin</div>
            <ul className="space-y-1">
              {(adminNav || []).map((n) => <NavItem key={n.to} {...n} />)}
            </ul>
          </div>
        )}
      </nav>

      <div className="p-3 border-t border-[#1f1f1f] shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="app-shell-dropdown-menu-trigger-180" asChild>
            <button
              data-testid="user-menu-btn"
              className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#141414] transition-colors text-left"
            >
              <Avatar className="h-8 w-8 border border-[#262626]">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#171717] text-[11px] text-zinc-300">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-rose-400/80" data-testid="current-user-role">
                  {ROLE_LABELS[role] || "User"}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0f0f0f] border-[#262626] text-zinc-200 w-56">
            <DropdownMenuLabel className="text-xs text-zinc-500">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#262626]" />
            <DropdownMenuItem
              data-testid="profile-btn"
              onClick={() => { setDrawerOpen(false); navTo("/profile"); }}
              className="focus:bg-[#1a1a1a] cursor-pointer"
            >
              <UserCircle2 className="h-4 w-4 mr-2" /> My profile
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="logout-btn"
              onClick={logout}
              className="focus:bg-[#1a1a1a] cursor-pointer text-rose-300 focus:text-rose-300"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  if (kiosk) {
    return (
      <div className="min-h-screen" data-testid="app-shell-kiosk">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Desktop: fixed side menu */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-[#1f1f1f] bg-[#0b0b0b] flex-col sticky top-0 h-screen">
        {sidebar}
      </aside>

      {/* Phones and tablets: the same menu as a slide-out drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu" data-testid="mobile-drawer">
          <button type="button" className="absolute inset-0 bg-black/70" aria-label="Close menu" onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-[82vw] max-w-xs h-full border-r border-[#1f1f1f] bg-[#0b0b0b] flex flex-col shadow-2xl">
            <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Close menu"
              className="absolute right-3 top-5 p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-[#141414]">
              <X className="h-5 w-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <header ref={headerRef} className="sticky top-0 z-20 glass border-b border-[#1f1f1f] px-4 lg:px-8 py-3 lg:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button type="button" onClick={() => setDrawerOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn"
              className="lg:hidden -ml-1 p-2 rounded-lg text-zinc-300 hover:text-white hover:bg-[#141414]">
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="eyebrow hidden sm:block">FullPowerAnalytics · by LIFTR</div>
              <h1 className="font-display text-lg lg:text-xl font-bold tracking-tight sm:mt-0.5 truncate" data-testid="page-title">{title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Global Admin is a cross-facility console. Hide the facility switcher on this
                surface so it doesn't confuse admins into thinking data is scoped to one gym. */}
            <FacilitySwitcher />
            <NoticesBell />
            <div className="hidden md:inline-flex items-center gap-2 text-xs text-zinc-500">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {loc.pathname.startsWith("/global-admin")
                ? "Cross-facility view"
                : `${ROLE_LABELS[role] || "User"} view`}
            </div>
          </div>
        </header>
        {atLeast(user?.role, "admin") && !loc.pathname.startsWith("/ghl") && <GhlConnectionBanner />}
        <div className={`flex-1 px-4 py-5 lg:px-8 lg:py-8 pb-24 lg:pb-8 ${viewOnly ? "view-only" : ""}`} data-view-only={viewOnly || undefined}>
          {viewOnly && (
            <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="view-only-banner">
              View only: you're not assigned to {activeFacility?.name || "this gym"}, so you can look but not change anything here.
              Fixes go through the gym team, or a correction request for a closed month.
            </div>
          )}
          <BirthdayPrompt />
          <Outlet />
        </div>
      </main>

      {/* Phones: bottom bar for the screens used most on the floor */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#1f1f1f] bg-[#0b0b0b]/95 backdrop-blur"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} aria-label="Quick links" data-testid="mobile-bottom-nav">
        <ul className="grid grid-cols-6">
          {BOTTOM_NAV.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink to={to} end={end}
                className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium ${isActive ? "text-white" : "text-zinc-500"}`}>
                <span className="relative">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                  {to === "/sales" && waitingCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] rounded-full bg-rose-600 px-1 text-[10px] leading-4 font-semibold text-white text-center">{waitingCount}</span>
                  )}
                </span>
                <span className="truncate max-w-full px-1">{label}</span>
              </NavLink>
            </li>
          ))}
          <li>
            <button type="button" onClick={() => setDrawerOpen(true)}
              className="w-full flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium text-zinc-500">
              <MoreHorizontal className="h-5 w-5" />
              <span>More</span>
            </button>
          </li>
        </ul>
      </nav>
      <PendingPaymentsReminder />
    </div>
  );
}

function NavItem({ to, label, icon: Icon, end, testid, badge = 0, badgeTitle = "waiting for a consult" }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        data-testid={testid}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${
            isActive
              ? "bg-[#171717] text-white border border-[#262626]"
              : "text-zinc-400 hover:text-white hover:bg-[#141414]"
          }`
        }
      >
        <Icon className="h-4 w-4" strokeWidth={2} />
        <span className="font-medium">{label}</span>
        {badge > 0 && (
          <span className="ml-auto rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-semibold text-white" title={to === "/leave" ? `${badge} leave request${badge > 1 ? "s" : ""} to review` : `${badge} ${badgeTitle}`}>{badge}</span>
        )}
      </NavLink>
    </li>
  );
}

/** Admins see this until every active gym is connected to its GoHighLevel account. */
function GhlConnectionBanner() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    listGhlConnections().then((d) => setSummary(d.summary), () => setSummary(null));
  }, []);
  if (!summary || summary.connected >= summary.active) return null;
  const missing = summary.active - summary.connected;
  return (
    <NavLink to="/ghl" className="flex items-center gap-2 border-b border-amber-500/20 bg-amber-500/10 px-8 py-2 text-xs text-amber-200 hover:bg-amber-500/15"
      data-testid="ghl-banner">
      <AlertTriangle className="h-3.5 w-3.5" />
      {missing} of {summary.active} gyms aren&apos;t fully connected to GoHighLevel, so their WhatsApp and email can&apos;t go out. Set them up →
    </NavLink>
  );
}
